import React from 'react';
import classNames from 'classnames';
import { SectionProps } from '../../utils/SectionProps';
import Button from '../elements/Button';
import ButtonGroup from '../elements/ButtonGroup';
import Image from '../elements/Image';
import SectionHeader from './partials/SectionHeader';
import { useTranslation } from '../../i18n/LanguageProvider';

const propTypes = {
  ...SectionProps.types
};

const defaultProps = {
  ...SectionProps.defaults
};

const applicationEmail = 'recrutamento@algorcode.com.br';

const Careers = ({
  className,
  topOuterDivider,
  bottomOuterDivider,
  topDivider,
  bottomDivider,
  hasBgColor,
  invertColor,
  ...props
}) => {
  const { t } = useTranslation();

  const hero = t('careers.hero');
  const reasons = t('careers.reasons');
  const openings = t('careers.openings');
  const process = t('careers.process');
  const finalCta = t('careers.finalCta');

  const openingsSubjectPrefix = openings.subjectPrefix || 'Candidatura - ';
  const defaultSubject = `${openingsSubjectPrefix}AlgorCode`;

  const heroEmailHref = `mailto:${applicationEmail}?subject=${encodeURIComponent(defaultSubject)}&body=${encodeURIComponent(
    'Oi time AlgorCode,\n\nEstou interessado(a) em fazer parte da equipe.\n\nMeu curriculo ou portfolio esta disponivel em:\n\nObrigado(a)!'
  )}`;

  return (
    <>
      <section
        {...props}
        className={classNames(
          'hero section center-content',
          topOuterDivider && 'has-top-divider',
          bottomOuterDivider && 'has-bottom-divider',
          hasBgColor && 'has-bg-color',
          invertColor && 'invert-color',
          className
        )}
      >
        <div className="container-sm">
          <div className={classNames('hero-inner section-inner pb-0', topDivider && 'has-top-divider', bottomDivider && 'has-bottom-divider')}>
            <div className="hero-content">
              <h1 className="mt-0 mb-16 reveal-from-bottom" data-reveal-delay="200">
                {hero.title}
              </h1>
              <div className="container-xs">
                <p className="m-0 mb-24 reveal-from-bottom" data-reveal-delay="300">
                  {hero.description}
                </p>
                <ButtonGroup className="reveal-from-bottom" data-reveal-delay="450">
                  <Button tag="a" color="primary" wideMobile href="#vagas">
                    {hero.primaryCta}
                  </Button>
                  <Button
                    tag="a"
                    color="dark"
                    wideMobile
                    href={heroEmailHref}
                  >
                    {hero.secondaryCta}
                  </Button>
                </ButtonGroup>
              </div>
            </div>
            <div
              className="hero-figure reveal-from-top illustration-element-01"
              data-reveal-value="20px"
              data-reveal-delay="600"
            >
              <Image
                className="has-shadow"
                src={require('./../../assets/images/careers-1.png')}
                alt="Equipe AlgorCode"
                width={896}
                height={504}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="section mt-32 pt-32 pb-32">
        <div className="container">
          <SectionHeader
            data={reasons.header}
            className="center-content pb-32"
          />
          <div className="tiles-wrap center-content">
            {reasons.highlights.map((highlight, index) => (
              <div
                className="tiles-item reveal-from-bottom px-8 py-16"
                data-reveal-delay={index * 100}
                key={highlight.title}
              >
                <div className="tiles-item-inner pt-0 pb-16 px-8">
                  <h4 className="mt-0 mb-8 text-color-primary">{highlight.title}</h4>
                  <p className="m-0 text-sm">{highlight.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section pt-32 pb-32" id="vagas">
        <div className="container">
          <SectionHeader
            data={openings.header}
            className="center-content"
          />
          <div className="tiles-wrap careers-openings">
            {openings.positions.map((opening, index) => (
              <div
                className="tiles-item reveal-from-bottom px-8 py-16"
                data-reveal-delay={index * 120}
                key={opening.title}
              >
                <div className="tiles-item-inner pt-0 pb-16 px-8 max-auto">
                  <div className="text-xxs text-color-primary fw-600 tt-u mb-12">{opening.type}</div>
                  <h4 className="mt-0 mb-12">{opening.title}</h4>
                  <p className="m-0 text-sm">{opening.description}</p>
                  <div className="mt-16">
                    {opening.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xxs text-color-primary fw-600 tt-u mr-8"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="mt-24">
                    <Button
                      tag="a"
                      color="primary"
                      wideMobile
                      href={`mailto:${applicationEmail}?subject=${encodeURIComponent(`${openingsSubjectPrefix}${opening.title}`)}`}
                    >
                      {openings.cta}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-32 pt-32 text-sm center-content">
            {t('careers.openings.fallback', { email: applicationEmail })}
          </p>
        </div>
      </section>

      <section className="section pt-32 pb-32 mb-32">
        <div className="container">
          <SectionHeader
            data={process.header}
            className="center-content"
          />
          <div className="tiles-wrap center-content">
            {process.steps.map((step, index) => (
              <div
                className="tiles-item reveal-from-bottom px-8 py-16"
                data-reveal-delay={index * 120}
                key={step.title}
              >
                <div className="tiles-item-inner pt-0 pb-16 px-8">
                  <div className="text-xxs text-color-primary fw-600 tt-u mb-8">
                    {t('careers.process.stepLabel', { step: index + 1 })}
                  </div>
                  <h4 className="mt-0 mb-8">{step.title}</h4>
                  <p className="m-0 text-sm">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="cta section center-content-mobile reveal-from-bottom mb-32 pb-32">
        <div className="container">
          <div className="cta-inner section-inner">
            <div className="cta-slogan">
              <h3 className="m-0 text-color-high">{finalCta.title}</h3>
              <p className="m-0 text-sm text-color-success mt-12">
                {finalCta.description}
              </p>
            </div>
            <div className="cta-action">
              <Button
                tag="a"
                color="light"
                wideMobile
                href={`mailto:${applicationEmail}?subject=${encodeURIComponent(defaultSubject)}`}
              >
                {finalCta.action}
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

Careers.propTypes = propTypes;
Careers.defaultProps = defaultProps;

export default Careers;
